import { useMemo, useState } from 'react';
import { actors } from '@/mocks/actors';
import ActorCard from '@/components/feature/ActorCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import Pagination from '@/components/base/Pagination';
import EmptyState from '@/components/base/EmptyState';
import usePageMeta from '@/hooks/usePageMeta';

const PAGE_SIZE = 6;

export default function Actors() {
  usePageMeta({
    title: '演员介绍 - 女友的妈妈',
    description:
      '《女友的妈妈》主演阵容与演员资料介绍，支持按演员姓名快速检索，查看每位演员的饰演角色与代表作品。',
    keywords: '女友的妈妈演员表,女友的妈妈演员介绍,主演阵容',
  });

  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const query = keyword.trim();
    if (!query) return actors;
    return actors.filter((actor) => actor.name.includes(query) || actor.role.includes(query));
  }, [keyword]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageItems = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const handleKeyword = (value: string) => {
    setKeyword(value);
    setPage(1);
  };

  return (
    <>
      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-8 md:py-10">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '演员介绍' }]} />
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mt-4">
            演员介绍
          </h1>
          <p className="text-sm md:text-base text-foreground-600 mt-3 max-w-2xl">
            认识《女友的妈妈》台前阵容。点击演员卡片，查看个人资料、饰演角色以及相关剧集与资讯。
          </p>
        </div>
      </section>

      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <div className="max-w-[1280px] mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="relative w-full sm:w-72">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                type="text"
                value={keyword}
                onChange={(event) => handleKeyword(event.target.value)}
                placeholder="输入演员姓名或角色名搜索"
                className="w-full pl-9 pr-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
              />
            </div>
            <p className="text-xs md:text-sm text-foreground-500">
              共 <span className="text-foreground-900 font-medium">{filtered.length}</span> 位演员
            </p>
          </div>

          {pageItems.length === 0 ? (
            <EmptyState
              icon="ri-user-search-line"
              title="未找到相关演员"
              description="换个名字试试，或点击下方按钮查看全部演员。"
              actionLabel="查看全部演员"
              onAction={() => handleKeyword('')}
            />
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {pageItems.map((actor) => (
                  <ActorCard key={actor.id} actor={actor} />
                ))}
              </div>
              <Pagination current={current} total={totalPages} onChange={setPage} />
            </>
          )}
        </div>
      </div>
    </>
  );
}